import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';

import VerticalSlider from '../item/slider_vertical'
import Progress from '../item/progress'

import { FONT_COLOR } from '../const/general'

const yAxisLabelWidth = 90;

export default function Heatmap(props) {

  const [series, setSeries] = useState([]);
  const [sliderValue, setSliderValue] = useState([0, props.distance]);
  const [allData, setAllData] = useState([]);
  const [dateAry, setDateAry] = useState([]);

  const [isSlider, setIsSlider] = useState(false);
  const [dataLength, setDataLength] = useState(0);
  const [isNoData, setIsNoData] = useState(false);
  const dataType = props.dataType;
  // チャートごとの一意のキー
  const chartId = useId();

  // 監視用Ref
  const sliderValueRef = useRef(sliderValue);

  // ローディング終了
  const loadingEnd = () => {
    props.setReady(prev => {
      const updated = { ...prev };
      props.isReadyKey.forEach(key => {
        if (key in updated) {
          updated[key] = true;
        }
      });
      return updated;
    });
  }

  // データ更新が完了したらローディング終了
  useEffect(() => {
    if (allData !== null) {
      const timer = setTimeout(() => {
        loadingEnd();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [allData]);


  useEffect(() => {
    if (props.dateArray === null ||
      props.allRiskDatetime === null) {
      setIsNoData(true)
      loadingEnd()
      return
    }
    if (props.dateArray && props.dateArray.length > 0
      && props.allRiskDatetime && props.allRiskDatetime.length > 0) {
      setChartData(getHeatmapData(props.dateArray, props.allRiskDatetime), props.dateArray);
    }
    setIsNoData(false)
  }, [props.dateArray, props.allRiskDatetime]);

  useEffect(() => {
    if (props.dateArray === null ||
      props.allRiskDatetime === null) {
      setIsNoData(true)
      loadingEnd()
      return
    }
    if (props.dateArray && props.dateArray.length > 0
      && props.allRiskDatetime && props.allRiskDatetime.length > 0) {
      setChartData(getHeatmapData(props.dateArray, props.allRiskDatetime), props.dateArray);
    }
    setIsNoData(false)

    return () => {
      ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
    };
  }, []);

  // スライダーが移動された場合
  useEffect(() => {
    sliderValueRef.current = sliderValue;
    setChartData(allData, dateAry)
  }, [sliderValue]);

  // ヒートマップ用の形式に変換
  const getHeatmapData = (date_ary, area_val_ary) => {
    const isOver = props.distance < area_val_ary.length
    const chartData = props.getHeatmapData(date_ary, area_val_ary, isOver);
    if (isOver) {
      // スクロールONの場合は制限件数を切り取って返す
      return chartData.slice(props.limitDataCount * -1);
    }
    return chartData
  }

  // チャート情報を格納
  const setChartData = (chartData, date_ary) => {
    const isOver = props.distance < chartData.length

    // sliderValueに応じてリスクの最大値を並べ替え
    let final_ary = []
    if (isOver) {
      // for (let i = sliderValue[1]; i > sliderValue[0] - 1; i--) {
      for (let i = sliderValueRef.current[1]; i > sliderValueRef.current[0] - 1; i--) {

        if (chartData.length > i) {
          final_ary.unshift(chartData[i])
        }
      }
    } else {
      final_ary = chartData
    }

    setAllData(chartData)
    setSeries(final_ary)
    setIsSlider(isOver)
    setDateAry(date_ary)
    setDataLength(chartData.length);
  }

  const options = useMemo(() => ({
    chart: {
      id: chartId,
      toolbar: { show: false }, animations: { enabled: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          // ローディング開始
          props.setReady(prev => {
            const updated = Object.fromEntries(
              Object.keys(prev).map(key => [key, false])
            );
            return updated;
          });
          const seriesIndex = config.seriesIndex;
          const dataPointIndex = config.dataPointIndex;
          if (seriesIndex === -1 || dataPointIndex === -1) return; // クリック対象がない場合ガード
          const clickedItem = series[seriesIndex].data[dataPointIndex];
          props.heetmapClickEvent(clickedItem)
        }
      },
      nullValueColor: '#0092d1'
    },
    dataLabels: { enabled: false },
    // grid: { show: false },
    legend: { show: false },
    grid: {
      padding: {
        left: 0,
        right: 0,
        top: -25,
        bottom: -15
      }
    },
    xaxis: {
      categories: dateAry,
      // labels: { show: false },
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          colors: FONT_COLOR,
          fontSize: '10px',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: true }
    },
    yaxis: {
      labels: {
        show: true,
        width: yAxisLabelWidth,
        style: {
          colors: FONT_COLOR,
          fontSize: '10px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontWeight: 100,
          cssClass: 'apexcharts-yaxis-label',
        },
        offsetX: -10,
        offsetY: 1,
        rotate: 0,
        formatter: (value) => {
          const str = String(value);
          // 8文字以上は省略
          if (str.length >= 8) {
            return str = str.slice(0, 3) + '…' + str.slice(-4);
          }
          return '　'.repeat(8 - str.length) + str;
        },
      }
    },
    tooltip: {
      y: {
        formatter: function (val, { series, seriesIndex, dataPointIndex, w }) {
          const data = w.config.series[seriesIndex].data[dataPointIndex];
          return data.originalValue;
        }
      }
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0,
        radius: 0,
        useFillColorAsStroke: false,
        colorScale: props.colorScale(dataType),
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            const data = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];
            return data.originalValue.toFixed(0);
          }
        }
      }
    },
    stroke: {
      show: true,
      curve: 'straight',
      lineCap: 'butt',
      colors: ["#F2F2F2"],
      width: 0.5,
      dashArray: 2,
    },
    annotations: {
      position: 'front',
      xaxis: [
        {
          x: dateAry[6],
          borderColor: '#0000FF',
          borderWidth: 1,
          strokeDashArray: 4,
          label: {
            show: false,
          }
        }
      ]
    },
  }), [dataType, dateAry, series]);

  // チャートのデータが更新されtら強制再描画
  useEffect(() => {
    if (series && series.length > 0) {
      ApexCharts.exec(chartId,
        "updateSeries", series);
    }
  }, [series]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          overflow: 'hidden', // スクロール非表示
        }}
      >
        {/* チャート部分 */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {!props.isReadyFlg ?
            <Progress />
            // <ChartSkeleton />
            :
            isNoData ?
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                対象データがありません
              </Typography>
              :
              <ReactApexChart
                options={options}
                series={series}
                type="heatmap"
                height="100%"
                width="100%"
                style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
              />
          }

        </Box>

        {/* スライダー部分 */}
        {isSlider &&
          <Box
            sx={{
              width: '20px', // スライダー幅
              marginLeft: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VerticalSlider
              dataLength={dataLength}
              value={sliderValue}
              setValue={setSliderValue}
              distance={props.distance}
              height={'90%'}
            />
          </Box>
        }
      </Box>
    </>
  );
}
